import type { TtsAdapter } from "@/adapters/contracts";
import { stopPlayback } from "@/audio/playback";
import type { RuntimeBus } from "@/runtime/bus";
import type { CapturedAudio } from "@/audio/capture";
import type { MessageRecord } from "@/runtime/events";
import {
  HeavyOperationError,
  startLlmTurnTask,
  startMicTurnTask,
} from "@/runtime/heavy-operation-client";

function messageFromUnknown(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }

  return typeof error === "string" ? error : "Unknown runtime error";
}

function isAbortError(error: unknown): boolean {
  return error instanceof DOMException && error.name === "AbortError";
}

export interface ConversationSessionOptions {
  bus: RuntimeBus;
  ttsAdapter: TtsAdapter;
  /**
   * Deliberately independent of the UI event bus.  The coordinator emits this
   * audit trail for every epoch boundary and for every stale result it drops.
   * It is important that a cancelled operation cannot look like a successful
   * turn merely because its worker completed after the user had moved on.
   */
  onTelemetry?: (event: ConversationCoordinatorTelemetry) => void;
}

export type ConversationLane = "stt" | "llm" | "tts";

export interface ConversationCoordinatorTelemetry {
  event:
    | "conversation.turn.begin"
    | "conversation.turn.cancel"
    | "conversation.result.accepted"
    | "conversation.result.discarded";
  turnId: number;
  epoch: number;
  lane?: ConversationLane;
  reason?: "superseded" | "interrupted" | "aborted";
}

/**
 * Orchestrates one conversation turn.
 *
 * Mic turn: mic audio → heavy worker (VAD + STT) → `stt.final`. A separate
 * caller (typically App.tsx handleRecognizedTranscript) then commits the
 * transcript to the thread and kicks off an assistant turn.
 *
 * Assistant turn: messages → heavy worker (LLM only, returns text) →
 * `llm.done` → TtsAdapter.speak(text) on main thread → `tts.audio` events
 * and `tts.done`. TTS never runs in the worker because OpenMOSS's vendored
 * tokenizer sandbox requires a main-thread DOM; cloud TTS adapters will also
 * run on the main thread for the same adapter-boundary reason.
 */
export class ConversationSession {
  private abortController: AbortController | null = null;
  private activeTaskAbort: (() => void) | null = null;
  private activeTtsAdapter: TtsAdapter | null = null;
  private epoch = 0;
  private nextTurnId = 0;
  private activeTurnId = 0;

  constructor(private options: ConversationSessionOptions) {}

  get bus(): RuntimeBus {
    return this.options.bus;
  }

  setTtsAdapter(adapter: TtsAdapter): void {
    this.options.ttsAdapter = adapter;
  }

  private publishTelemetry(event: ConversationCoordinatorTelemetry): void {
    this.options.onTelemetry?.(event);
  }

  private isCurrent(turnId: number, epoch: number, signal: AbortSignal, lane: ConversationLane): boolean {
    const current = !signal.aborted && this.activeTurnId === turnId && this.epoch === epoch;
    if (!current) {
      this.publishTelemetry({
        event: "conversation.result.discarded",
        turnId,
        epoch,
        lane,
        reason: signal.aborted ? "aborted" : "superseded",
      });
    }
    return current;
  }

  private beginOperation(): { signal: AbortSignal; turnId: number; epoch: number } {
    const priorTurnId = this.activeTurnId;
    const priorEpoch = this.epoch;
    this.activeTaskAbort?.();
    this.activeTaskAbort = null;
    this.activeTtsAdapter?.interrupt().catch(() => undefined);
    this.activeTtsAdapter = null;
    this.abortController?.abort();
    if (priorTurnId) {
      this.publishTelemetry({
        event: "conversation.turn.cancel",
        turnId: priorTurnId,
        epoch: priorEpoch,
        reason: "superseded",
      });
    }
    this.abortController = new AbortController();
    this.epoch += 1;
    this.activeTurnId = ++this.nextTurnId;
    this.publishTelemetry({
      event: "conversation.turn.begin",
      turnId: this.activeTurnId,
      epoch: this.epoch,
    });
    return { signal: this.abortController.signal, turnId: this.activeTurnId, epoch: this.epoch };
  }

  async runMicTurn(capturedAudio: CapturedAudio): Promise<void> {
    const { signal, turnId, epoch } = this.beginOperation();

    try {
      const task = startMicTurnTask(
        {
          durationMs: capturedAudio.durationMs,
          mono: capturedAudio.mono,
          sampleRate: capturedAudio.sampleRate,
        },
        {
          onRuntimeEvent: (event) => {
            if (this.isCurrent(turnId, epoch, signal, "stt")) {
              this.options.bus.publish(event);
            }
          },
        },
      );
      this.activeTaskAbort = task.abort;
      const result = await task.promise;
      this.activeTaskAbort = null;

      if (!this.isCurrent(turnId, epoch, signal, "stt")) {
        return;
      }

      this.options.bus.publish({ type: "stt.final", text: result.transcript });
      this.publishTelemetry({ event: "conversation.result.accepted", turnId, epoch, lane: "stt" });
    } catch (error) {
      this.activeTaskAbort = null;

      if (!this.isCurrent(turnId, epoch, signal, "stt") || isAbortError(error)) {
        return;
      }

      this.options.bus.publish({
        type: "error",
        domain: error instanceof HeavyOperationError ? error.domain : "stt",
        message: messageFromUnknown(error),
      });
    }
  }

  async runAssistantTurn(messages: MessageRecord[]): Promise<void> {
    const { signal, turnId, epoch } = this.beginOperation();

    let responseText: string | null = null;

    try {
      const task = startLlmTurnTask(messages, {
        onRuntimeEvent: (event) => {
          if (this.isCurrent(turnId, epoch, signal, "llm")) {
            this.options.bus.publish(event);
          }
        },
      });
      this.activeTaskAbort = task.abort;
      const result = await task.promise;
      this.activeTaskAbort = null;

      if (!this.isCurrent(turnId, epoch, signal, "llm")) {
        return;
      }

      responseText = result.text;
      this.publishTelemetry({ event: "conversation.result.accepted", turnId, epoch, lane: "llm" });
    } catch (error) {
      this.activeTaskAbort = null;

      if (!this.isCurrent(turnId, epoch, signal, "llm") || isAbortError(error)) {
        return;
      }

      const domain = error instanceof HeavyOperationError ? error.domain : "llm";
      this.options.bus.publish({
        type: "error",
        domain,
        message: messageFromUnknown(error),
      });
      return;
    }

    if (!responseText || !this.isCurrent(turnId, epoch, signal, "tts")) {
      return;
    }

    // Hand off to the TTS adapter on the main thread. The adapter publishes
    // `tts.audio` + `tts.done` to the bus itself, so the rest of the app
    // observes the same event stream regardless of adapter source.
    const adapter = this.options.ttsAdapter;
    this.activeTtsAdapter = adapter;
    try {
      await adapter.speak(responseText, { abortSignal: signal });
      if (this.isCurrent(turnId, epoch, signal, "tts")) {
        this.publishTelemetry({ event: "conversation.result.accepted", turnId, epoch, lane: "tts" });
      }
    } catch (error) {
      if (!this.isCurrent(turnId, epoch, signal, "tts") || isAbortError(error)) {
        return;
      }
      this.options.bus.publish({
        type: "error",
        domain: "tts",
        message: messageFromUnknown(error),
      });
      // The adapter is expected to publish `tts.done` in its own error path;
      // don't re-emit it here.
    } finally {
      this.activeTtsAdapter = null;
    }
  }

  interrupt(): void {
    const turnId = this.activeTurnId;
    const epoch = this.epoch;
    this.epoch += 1;
    this.activeTaskAbort?.();
    this.activeTaskAbort = null;
    const adapter = this.activeTtsAdapter;
    this.activeTtsAdapter = null;
    adapter?.interrupt().catch(() => undefined);
    this.abortController?.abort();
    stopPlayback();
    if (turnId) {
      this.publishTelemetry({
        event: "conversation.turn.cancel",
        turnId,
        epoch,
        reason: "interrupted",
      });
    }
    this.options.bus.publish({ type: "vad.barge_in" });
    this.options.bus.publish({ type: "tts.done" });
  }
}
